/**
 * Keyboard shortcuts — the command catalog's display strings and the shortcuts
 * dialog's own copy.
 *
 * The keys under `general`/`navigation`/`viewer`/`designPoints`/`reportWriteup`
 * mirror `src/commands/catalog.ts`: each command names its `titleKey` here, so
 * adding a command means adding a title in this file and its two siblings.
 *
 * No `{{count}}` interpolation anywhere on purpose. i18next plurals need
 * `_one`/`_other` variants in `en` that ja and zh-TW correctly omit, which
 * reads as structural drift to `parity.test.ts`; every string here is phrased
 * to work at any number instead.
 */
export default {
  groups: {
    general: 'General',
    navigation: 'Navigation',
    viewer: '3D viewer',
    editing: 'Editing',
  },

  // Where a shortcut works. Shown as a badge on any non-global command.
  scopes: {
    global: 'Anywhere',
    viewer: 'In the 3D viewer',
    designPoints: 'In the design points table',
    reportWriteup: 'In the report editor',
  },

  // Why a listed shortcut will not fire right now. Keys match `BindingStatus`
  // in src/commands/resolve.ts.
  status: {
    'scope-inactive': 'Not on this screen',
    shadowed: 'Another command is using this key',
    'handler-disabled': 'Not available right now',
    'no-handler': 'Not available here',
  },

  shortcut: {
    // Joins the steps of a two-key sequence: "G then H". Keep it short — it
    // renders between keycaps.
    sequenceConnector: 'then',
    unbound: 'No shortcut',
  },

  recorder: {
    rebind: 'Change',
    cancel: 'Cancel',
    reset: 'Reset',
    listening: 'Press a key…',
    reserved:
      'The 3D viewer already uses that key for something else. Pick another.',
    unusable:
      'That key cannot be a shortcut on its own. Try adding Shift or Ctrl.',
    taken: '{{shortcut}} already runs {{command}}.',
    takeover: 'Use it anyway',
  },

  dialog: {
    title: 'Keyboard shortcuts',
    description:
      'Press Change next to a shortcut, then press the keys you want. While recording, Esc cancels and Backspace removes the shortcut.',
    customized: 'Some shortcuts no longer match the defaults.',
    resetAll: 'Reset all',
  },

  conflicts: {
    heading: 'Two commands share one key',
    row: '{{shortcut}} — {{commands}}',
    // Joins the command names in a conflict row.
    separator: ', ',
  },

  general: {
    showShortcuts: {
      title: 'Show keyboard shortcuts',
      description: 'Opens this list.',
    },
    toggleAssistant: {
      title: 'Show or hide the assistant',
      description: 'Opens the chat panel on the right.',
    },
    closeOverlay: {
      title: 'Close the open dialog',
      description: 'Closes whatever is on top.',
    },
  },

  navigation: {
    goHome: { title: 'Go to home' },
    goMeshgen: { title: 'Go to MeshGen' },
    goSimops: { title: 'Go to SimOps' },
  },

  viewer: {
    resetCamera: {
      title: 'Reset the camera',
      description: 'Frames the whole model again.',
    },
  },

  designPoints: {
    selectNext: { title: 'Select the next design point' },
    selectPrevious: { title: 'Select the previous design point' },
    closeDetail: { title: 'Close the detail panel' },
  },

  reportWriteup: {
    save: {
      title: 'Save the report',
      description: 'Works while you are typing in the editor.',
    },
  },
};
